import { Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import {
  HardBaseFormControlComponent,
  HardBtnComponent,
  HardFormModule,
} from "@hardcode-kz/ui";

import { IMenuItemModel } from "@models/core/menu";
import { MenuService } from "@dmms/core/menu";
import { ClientTypeEnum } from "@models/core/client-type";
import { take } from "rxjs/operators";
import { catchError, of } from "rxjs";
import { UIMenuItemComponent } from "../ui-menu-item/ui-menu-item.component";
import { UIMenuItemModalComponent } from "../ui-menu-item-modal/ui-menu-item-modal.component";
import { LangEnum } from "@models/interface-management/localization";
import { LocalizationService } from "@dmms/core/localization";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { AdministrationEndpoints } from "@dmms/shared/data-access/administration/types/administration-endpoints";

@Component({
  selector: "ui-menu-items",
  standalone: true,
  imports: [HardBtnComponent, UIMenuItemComponent, HardFormModule, FormsModule],
  templateUrl: "./ui-menu-items.component.html",
  styleUrl: "./ui-menu-items.component.scss",
})
export class UIMenuItemsComponent
  extends HardBaseFormControlComponent
  implements OnInit
{
  private readonly localizationService = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  dicType = AdministrationEndpoints.Menu;
  data: IMenuItemModel[];
  filteredItems: IMenuItemModel[];

  searchTerm = signal("");
  lang = signal<LangEnum>(LocalizationService.getLang());

  constructor(
    private dialog: MatDialog,
    private menuService: MenuService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.localizationService
      .subscribeCurrentLang()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang) => {
        this.lang.set(lang);
      });

    this.loadData();
  }

  loadData() {
    this.menuService
      .getMenuBySlug(this.form.get("slug").value, ClientTypeEnum.All)
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .subscribe((response) => {
        this.data = (response?.value?.items || []).map((item) => {
          item.show = (this.filteredItems || []).find(
            (i) => i.id == item.id,
          )?.show;
          return item;
        });

        this.filter(this.searchTerm(), true);
      });
  }

  filter(searchTerm: string, init = false): void {
    function search(menuItem: IMenuItemModel, lang: LangEnum): boolean {
      return (
        menuItem.name[lang].toLowerCase().indexOf(searchTerm.toLowerCase()) >
          -1 ||
        menuItem.url.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1
      );
    }

    function processItem(item: IMenuItemModel, lang: LangEnum): IMenuItemModel {
      if (!item.children?.length) {
        item.filteredChildren = [];
        item.isSearchShow = search(item, lang);
      } else {
        const processedChildren = item.children.map((child) =>
          processItem(child, lang),
        );
        item.filteredChildren = processedChildren.filter((x) => x.isSearchShow);
        item.isSearchShow =
          item.filteredChildren.length > 0 || search(item, lang);
      }
      return item;
    }

    (this.data || []).forEach((menu) => processItem(menu, this.lang()));

    this.filteredItems = (this.data || [])
      .filter((m) => m.isSearchShow)
      .map((item) => ({ ...item, children: item.filteredChildren }));

    (this.filteredItems || []).forEach((item) => {
      item.show = searchTerm?.length > 0 ? true : init ? item.show : false;
    });
  }

  open(item?: IMenuItemModel) {
    this.dialog
      .open(UIMenuItemModalComponent, {
        width: "580px",
        height: "715px",
        data: {
          menuId: this.form.get("id").value,
          slug: this.form.get("slug").value,
          value: item,
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
  }
}
